import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { getUserTimeline, depositPurchasePoints } from '../services/api'
import { useAuditLog, AUDIT_ACTIONS } from '../services/auditLog'
import { useAuth } from './auth/AuthProvider'

const PurchaseContainer = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
`

const ReceiptContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const Receipt = styled.div`
  background: white;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  padding: 1.5rem;
  font-family: 'Courier New', monospace;
  box-shadow: 0 2px 4px ${theme.color.shadow.light};
  max-width: 400px;
  margin: 0 auto;
`

const ReceiptHeader = styled.div`
  text-align: center;
  border-bottom: 2px dashed ${theme.color.border.light};
  padding-bottom: 1rem;
  margin-bottom: 1rem;
`

const StoreName = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
`

const DateTime = styled.div`
  font-size: 1.2rem;
  color: ${theme.color.text.grey};
  margin-bottom: 0.25rem;
`

const TransactionId = styled.div`
  font-size: 1rem;
  color: ${theme.color.text.grey};
`

const ItemsSection = styled.div`
  margin-bottom: 1rem;
`

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
`

const ItemName = styled.div`
  flex: 1;
  margin-right: 1rem;
`

const ItemQuantity = styled.div`
  margin-right: 1rem;
  text-align: center;
  min-width: 30px;
`

const ItemPrice = styled.div`
  text-align: right;
  min-width: 60px;
`

const TotalSection = styled.div`
  border-top: 2px dashed ${theme.color.border.light};
  padding-top: 1rem;
  margin-top: 1rem;
`

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 1.4rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
`

const PointsEarned = styled.div`
  border-top: 1px dashed ${theme.color.border.light};
  padding-top: 0.5rem;
  margin-top: 0.5rem;
  text-align: center;
  font-size: 1.2rem;
  color: #28a745;
  font-weight: bold;
`

const DepositButtonContainer = styled.div`
  border-top: 1px dashed ${theme.color.border.light};
  padding-top: 1rem;
  margin-top: 1rem;
  text-align: center;
  position: relative;
  display: inline-block;
  width: 100%;
`

const DepositButton = styled.button<{ $isDepositing?: boolean }>`
  background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: ${props => props.$isDepositing ? 'wait' : 'pointer'};
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5a6268 0%, #495057 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover:not(:disabled)::before {
    left: 100%;
  }
  
`


const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-style: italic;
`

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
`

const PaginationButton = styled.button<{ disabled?: boolean }>`
  padding: 0.75rem 1.5rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 4px;
  background: ${props => props.disabled ? theme.color.background.lightGrey : 'white'};
  color: ${props => props.disabled ? theme.color.text.grey : theme.color.text.base};
  font-size: 1.4rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not([disabled]) {
    background: ${theme.color.background.light};
    border-color: ${theme.color.accent.primary};
  }

  &:disabled {
    opacity: 0.5;
  }
`

const PageInfo = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.base};
  font-weight: 500;
  padding: 0 1rem;
`


const LoadingSpinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 2px solid #ffffff;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const StatusTooltip = styled.div<{ $show: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 1rem;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${props => props.$show ? 1 : 0};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #333;
  }
`

interface PurchaseHistoryProps {
  userData: any
  onUpdate?: () => void
  refreshTimestamp?: number
}

interface PurchaseTransaction {
  transaction_id: string
  pos_transaction_id?: string | null
  date: string
  time: string
  store_name: string
  items: any[]
  payments: any[]
  subtotal: number
  points_earned?: number
}

const PurchaseHistory: React.FC<PurchaseHistoryProps> = ({ userData, onUpdate, refreshTimestamp }) => {
  const { isAdmin } = useAuth()
  const { logEvent } = useAuditLog()
  const [purchases, setPurchases] = useState<PurchaseTransaction[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [depositingTransactions, setDepositingTransactions] = useState<Set<string>>(new Set())
  const [hoveredTransaction, setHoveredTransaction] = useState<string | null>(null)
  const [depositStatus, setDepositStatus] = useState<{ [key: string]: string }>({})
  const itemsPerPage = 5

  useEffect(() => {
    if (userData?.user?.external_id) {
      loadPurchaseHistory()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.user?.external_id, refreshTimestamp])

  // Separate effect to reset page only when user changes (not on refresh)
  useEffect(() => {
    if (userData?.user?.external_id) {
      setCurrentPage(1) // Reset to first page when user changes
    }
  }, [userData?.user?.external_id])


  const handleDepositPoints = async (transactionId: string, subtotal: number) => {
    if (!userData?.user?.id) return
    
    const pointsToDeposit = Math.ceil(subtotal * 4)
    
    setDepositingTransactions(prev => new Set(prev).add(transactionId))
    setDepositStatus(prev => ({ ...prev, [transactionId]: 'Connecting to SessionM...' }))
    
    try {
      setDepositStatus(prev => ({ ...prev, [transactionId]: 'Processing point deposit...' }))
      const depositResponse = await depositPurchasePoints(userData.user.id, pointsToDeposit, transactionId)
      
      // Log the actual response for debugging
      console.log('Deposit API Response:', depositResponse)
      
      // If we get here without an error, the deposit was successful
      setDepositStatus(prev => ({ ...prev, [transactionId]: 'Logging action...' }))
      
      // Log audit event
      await logEvent({
        tabName: 'Purchase History',
        action: AUDIT_ACTIONS.POINTS_DEPOSITED,
        details: {
          source: 'purchase_deposit',
          points_amount: pointsToDeposit,
          transaction_id: transactionId,
          deposit_response: depositResponse
        },
        targetUserId: userData.user.id,
        targetUserEmail: userData.user?.email
      })
      
      setDepositStatus(prev => ({ ...prev, [transactionId]: 'Refreshing data...' }))
      
      // Clear any cached data and refresh purchase history
      await loadPurchaseHistory(true) // Pass true to force cache refresh
      
      setDepositStatus(prev => ({ ...prev, [transactionId]: 'Success! Points deposited.' }))
      
      // Clear success message after 2 seconds
      setTimeout(() => {
        setDepositStatus(prev => {
          const newStatus = { ...prev }
          delete newStatus[transactionId]
          return newStatus
        })
      }, 2000)
      
    } catch (err) {
      console.error('Failed to deposit points:', err)
      setError(err instanceof Error ? err.message : 'Failed to deposit points')
      setDepositStatus(prev => ({ ...prev, [transactionId]: 'Error: Deposit failed' }))
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setDepositStatus(prev => {
          const newStatus = { ...prev }
          delete newStatus[transactionId]
          return newStatus
        })
      }, 3000)
    } finally {
      setDepositingTransactions(prev => {
        const newSet = new Set(prev)
        newSet.delete(transactionId)
        return newSet
      })
    }
  }

  const loadPurchaseHistory = async (forceRefresh = false) => {
    if (!userData?.user?.external_id) return

    setIsLoading(true)
    setError('')

    try {
      // Add cache-busting parameter when forcing refresh
      const timelineData = await getUserTimeline(userData.user.external_id, forceRefresh)
      console.log('Timeline API response:', timelineData)
      
      const events = timelineData.result || []
      
      // Find purchase transactions
      const purchaseEvents = events.filter((event: any) => 
        event.event_stream_payload?.event_type_slug === 'PURCHASE_TRANSACTION'
      )
      
      // Find point deposits to match with purchases
      const pointEvents = events.filter((event: any) => 
        event.event_stream_payload?.event_type_slug === 'INCENTIVES_POINT_DEPOSIT'
      )
      
      const excludedItems = ['pack', 'package', 'membership', 'classes', 'gift', 'gift card', 'no show']
      
      const processedPurchases = purchaseEvents.map((purchase: any) => {
        const eventData = purchase.event_stream_payload || {}
        const transactionId = eventData.id
        
        // Find matching point event - check both TransactionID and transaction_id fields
        const matchingPointEvent = pointEvents.find((pointEvent: any) => 
          pointEvent.event_stream_payload?.TransactionID === transactionId ||
          pointEvent.event_stream_payload?.transaction_id === transactionId
        )
        
        const pointsEarned = matchingPointEvent?.event_stream_payload?.Modification || 0
        
        return {
          transaction_id: transactionId,
          pos_transaction_id: eventData.pos_transaction_id || null,
          date: new Date(purchase.timestamp).toLocaleDateString(),
          time: new Date(purchase.timestamp).toLocaleTimeString(),
          store_name: eventData.store?.name || 'Barry\'s Bootcamp',
          items: eventData.items || [],
          payments: eventData.payments || [],
          subtotal: eventData.subtotal || 0,
          points_earned: pointsEarned
        }
      }).filter((purchase: PurchaseTransaction) => {
        // Filter out transactions with subtotal <= 0
        if (purchase.subtotal <= 0) return false

        // Filter out transactions containing excluded items
        const hasExcludedItem = purchase.items.some((item: any) => {
          const displayName = (item.item_pos?.display_name || '').toLowerCase()
          return excludedItems.some(excludedItem => displayName.includes(excludedItem))
        })

        return !hasExcludedItem
      })

      // Deduplicate by transaction_id (SessionM timeline can return the same event multiple times)
      const seen = new Set<string>()
      const deduped = processedPurchases.filter((p: PurchaseTransaction) => {
        if (!p.transaction_id || seen.has(p.transaction_id)) return false
        seen.add(p.transaction_id)
        return true
      })
      processedPurchases.length = 0
      processedPurchases.push(...deduped)

      // Demo mode: For j.kliman@barrys.com, add demo fuel bar and retail purchases
      if (userData?.user?.email === 'j.kliman@barrys.com') {
        console.log('🎬 Purchase History Demo Mode: Adding demo purchases for j.kliman@barrys.com')
        const demoPurchases: PurchaseTransaction[] = [
          {
            transaction_id: 'demo-fuel-bar-1',
            pos_transaction_id: 'POS-DEMO-001',
            date: new Date('2025-01-20').toLocaleDateString(),
            time: '7:45 AM',
            store_name: "Barry's NoMad",
            items: [
              { item_pos: { display_name: 'Fuel Shake - Chocolate Peanut Butter' }, quantity: 1, subtotal: 12.00 },
              { item_pos: { display_name: 'Banana' }, quantity: 1, subtotal: 2.00 }
            ],
            payments: [{ payment_type: 'Credit Card', amount: 14.00 }],
            subtotal: 14.00,
            points_earned: 56
          },
          {
            transaction_id: 'demo-retail-1',
            pos_transaction_id: 'POS-DEMO-002',
            date: new Date('2025-01-18').toLocaleDateString(),
            time: '6:30 PM',
            store_name: "Barry's NoMad",
            items: [
              { item_pos: { display_name: "Barry's Classic Tank - Black (M)" }, quantity: 1, subtotal: 48.00 },
              { item_pos: { display_name: "Barry's Grip Socks" }, quantity: 1, subtotal: 18.00 }
            ],
            payments: [{ payment_type: 'Credit Card', amount: 66.00 }],
            subtotal: 66.00,
            points_earned: 264
          },
          {
            transaction_id: 'demo-fuel-bar-2',
            pos_transaction_id: 'POS-DEMO-003',
            date: new Date('2025-01-15').toLocaleDateString(),
            time: '8:00 AM',
            store_name: "Barry's NoMad",
            items: [
              { item_pos: { display_name: 'Cold Brew Coffee' }, quantity: 1, subtotal: 5.00 },
              { item_pos: { display_name: 'Protein Bar - Almond' }, quantity: 2, subtotal: 8.00 }
            ],
            payments: [{ payment_type: 'Credit Card', amount: 13.00 }],
            subtotal: 13.00,
            points_earned: 52
          },
          {
            transaction_id: 'demo-retail-2',
            pos_transaction_id: 'POS-DEMO-004',
            date: new Date('2025-01-10').toLocaleDateString(),
            time: '5:45 PM',
            store_name: "Barry's NoMad",
            items: [
              { item_pos: { display_name: "Barry's Water Bottle - Red" }, quantity: 1, subtotal: 32.00 }
            ],
            payments: [{ payment_type: 'Credit Card', amount: 32.00 }],
            subtotal: 32.00,
            points_earned: 128
          }
        ]
        processedPurchases.unshift(...demoPurchases)
      }

      setPurchases(processedPurchases)
    } catch (err) {
      console.error('Failed to load purchase history:', err)
      setError(err instanceof Error ? err.message : 'Failed to load purchase history')
    } finally {
      setIsLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    // SessionM amounts are already in dollars, not cents
    return `$${amount.toFixed(2)}`
  }

  // Calculate pagination
  const totalPages = Math.ceil(purchases.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentPurchases = purchases.slice(startIndex, endIndex)

  const goToPage = (page: number) => {
    setCurrentPage(page)
  }

  const goToPrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const goToNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  if (!userData || !userData.user) {
    return null
  }

  return (
    <PurchaseContainer>
      <Title>Purchase History</Title>
      
      {isLoading && depositingTransactions.size === 0 ? (
        <LoadingMessage>Loading purchase history...</LoadingMessage>
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : purchases.length === 0 ? (
        <NoDataMessage>No purchase history found</NoDataMessage>
      ) : (
        <div>
          <ReceiptContainer>
            {currentPurchases.map((purchase, index) => (
            <Receipt key={index}>
              <ReceiptHeader>
                <StoreName>{purchase.store_name}</StoreName>
                <DateTime>{purchase.date}</DateTime>
                <DateTime>{purchase.time}</DateTime>
                <TransactionId>Transaction: {purchase.transaction_id}</TransactionId>
                {purchase.pos_transaction_id && (
                  <TransactionId>Order Number: {purchase.pos_transaction_id}</TransactionId>
                )}
              </ReceiptHeader>
              
              <ItemsSection>
                {purchase.items.map((item: any, itemIndex: number) => (
                  <ItemRow key={itemIndex}>
                    <ItemName>{item.item_pos?.display_name || 'Unknown Item'}</ItemName>
                    <ItemQuantity>x{item.quantity || 1}</ItemQuantity>
                    <ItemPrice>{formatCurrency(item.subtotal || 0)}</ItemPrice>
                  </ItemRow>
                ))}
              </ItemsSection>
              
              <TotalSection>
                {(() => {
                  const subtotal = purchase.subtotal;
                  const total = purchase.payments.reduce((sum: number, payment: any) => sum + (payment.amount || 0), 0);
                  const tax = total - subtotal;
                  
                  console.log('Receipt amounts debug:', { 
                    subtotal, 
                    total, 
                    tax, 
                    payments: purchase.payments,
                    subtotalAfterFormat: formatCurrency(subtotal),
                    totalAfterFormat: formatCurrency(total),
                    taxAfterFormat: formatCurrency(tax)
                  });
                  
                  return (
                    <>
                      <TotalRow>
                        <div>SUBTOTAL:</div>
                        <div>{formatCurrency(subtotal)}</div>
                      </TotalRow>
                      {tax > 0 && (
                        <TotalRow>
                          <div>TAX:</div>
                          <div>{formatCurrency(tax)}</div>
                        </TotalRow>
                      )}
                      <TotalRow>
                        <div>TOTAL:</div>
                        <div>{formatCurrency(total)}</div>
                      </TotalRow>
                    </>
                  );
                })()}
                
                {(purchase.points_earned || 0) > 0 ? (
                  <PointsEarned>
                    Points Deposited: {purchase.points_earned || 0}
                  </PointsEarned>
                ) : isAdmin ? (
                  <DepositButtonContainer>
                    <DepositButton
                      onClick={() => handleDepositPoints(purchase.transaction_id, purchase.subtotal)}
                      disabled={depositingTransactions.has(purchase.transaction_id)}
                      $isDepositing={depositingTransactions.has(purchase.transaction_id)}
                      onMouseEnter={() => setHoveredTransaction(purchase.transaction_id)}
                      onMouseLeave={() => setHoveredTransaction(null)}
                    >
                      {depositingTransactions.has(purchase.transaction_id) && (
                        <LoadingSpinner />
                      )}
                      {depositingTransactions.has(purchase.transaction_id)
                        ? 'Depositing...'
                        : `Deposit ${Math.ceil(purchase.subtotal * 4)} Points?`}
                    </DepositButton>
                    
                    <StatusTooltip 
                      $show={(
                        hoveredTransaction === purchase.transaction_id && 
                        !depositingTransactions.has(purchase.transaction_id)
                      ) || (
                        depositingTransactions.has(purchase.transaction_id) && 
                        !!depositStatus[purchase.transaction_id]
                      )}
                    >
                      {depositingTransactions.has(purchase.transaction_id)
                        ? depositStatus[purchase.transaction_id] || 'Processing...'
                        : `Click to deposit ${Math.ceil(purchase.subtotal * 4)} points for this purchase`
                      }
                    </StatusTooltip>
                  </DepositButtonContainer>
                ) : null}
              </TotalSection>
            </Receipt>
          ))}
          </ReceiptContainer>
          
          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton 
                onClick={goToPrevious} 
                disabled={currentPage === 1}
              >
                Previous
              </PaginationButton>
              
              <PageInfo>
                Page {currentPage} of {totalPages} ({purchases.length} total)
              </PageInfo>
              
              <PaginationButton 
                onClick={goToNext} 
                disabled={currentPage === totalPages}
              >
                Next
              </PaginationButton>
            </PaginationContainer>
          )}
        </div>
      )}
    </PurchaseContainer>
  )
}

export default PurchaseHistory
